import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSiteNames } from '../../data/hooks';
import { dependantsIndex, deviceStatic, useStaticVersion } from '../../realtime/deviceStatic';
import { useDeviceLive } from '../../realtime/hooks';
import type { DeviceStatic } from '../../realtime/types';
import { Card, PageHeader, PresenceBadge, ProtocolTag } from '../../ui/components';
import { formatDateTime, formatNumber, formatRelative } from '../../ui/format';
import { usePresenceCounts } from '../devices/Topology';

const COLS = 'minmax(140px,1.3fr) 90px 80px minmax(120px,1fr) 120px 130px 70px 70px 70px 110px 110px';

/**
 * Field devices other devices are reached through (e.g. radio concentrators), from the scoped registry already in the
 * browser: live status, last seen, cellular signal and the live presence of the devices behind each one. Nothing is
 * fetched per row; counts refresh at most once per second.
 */
export function ConcentratorsPage() {
  const { t } = useTranslation();
  const version = useStaticVersion((s) => s.version);
  const siteNames = useSiteNames();
  const rows = useMemo(() => {
    const deps = dependantsIndex();
    const out: { d: DeviceStatic; ids: string[] }[] = [];
    for (const d of deviceStatic.values())
      if (deps.has(d.id) || d.deviceType === 'controller') out.push({ d, ids: deps.get(d.id) ?? [] });
    return out.sort((a, b) => a.d.name.localeCompare(b.d.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- version marks registry changes
  }, [version]);
  const allIds = useMemo(() => rows.flatMap((r) => r.ids), [rows]);
  const hubIds = useMemo(() => rows.map((r) => r.d.id), [rows]);
  const nodes = usePresenceCounts(allIds);
  const hubs = usePresenceCounts(hubIds);
  return (
    <div className="page">
      <PageHeader
        title={t('infrastructure.title')}
        subtitle={t('infrastructure.summary', {
          concentrators: formatNumber(hubs.total),
          online: formatNumber(hubs.online),
          nodes: formatNumber(nodes.total),
        })}
      />
      <Card>
        {rows.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            {t('infrastructure.empty')}
          </p>
        ) : (
          <div className="table-scroll">
            <div className="vhead" style={{ gridTemplateColumns: COLS, minWidth: 1100 }} role="row">
              <span>{t('common.name')}</span>
              <span>{t('devices.columns.model')}</span>
              <span>{t('devices.columns.protocol')}</span>
              <span>{t('common.site')}</span>
              <span>{t('common.status')}</span>
              <span>{t('infrastructure.columns.lastSeen')}</span>
              <span className="num">{t('infrastructure.columns.signal')}</span>
              <span className="num">{t('infrastructure.columns.online')}</span>
              <span className="num">{t('infrastructure.columns.offline')}</span>
              <span className="num">{t('infrastructure.columns.unknown')}</span>
              <span className="num">{t('infrastructure.columns.nodes')}</span>
            </div>
            {rows.map((r) => (
              <Row key={r.d.id} d={r.d} ids={r.ids} site={siteNames.get(r.d.siteId) ?? ''} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Row({ d, ids, site }: { d: DeviceStatic; ids: string[]; site: string }) {
  const presence = useDeviceLive(d.id, (x) => x?.presence);
  const seen = useDeviceLive(d.id, (x) => x?.lastSeenAt);
  const signal = useDeviceLive(d.id, (x) => x?.metrics?.['network.signal']);
  const c = usePresenceCounts(ids);
  return (
    <Link
      to={`/devices/${d.id}`}
      className="vrow static-row"
      style={{ gridTemplateColumns: COLS, minWidth: 1100 }}
    >
      <span className="ellipsis" style={{ fontWeight: 550 }}>
        {d.name}
      </span>
      <span className="ellipsis muted">{d.model ?? '—'}</span>
      <span>
        <ProtocolTag protocol={d.protocol} />
      </span>
      <span className="ellipsis muted">{site}</span>
      <span>
        <PresenceBadge presence={presence} />
      </span>
      <span className="muted small" title={seen ? formatDateTime(seen) : undefined}>
        {seen ? formatRelative(seen) : '—'}
      </span>
      <span className="num">{signal !== undefined ? formatNumber(signal) : '—'}</span>
      <span className="num">{formatNumber(c.online)}</span>
      <span className="num">{formatNumber(c.offline)}</span>
      <span className="num">{formatNumber(c.unknown)}</span>
      <span className="num strong">{formatNumber(c.total)}</span>
    </Link>
  );
}
